// ═══════════════════════════════════════════
// Email types.
// ═══════════════════════════════════════════

/** Outcome of a "send me a test mail" request — shared by every transport. */
export interface TestEmailResult {
    ok: boolean;
    error?: string;
    tested_at: string;
}

// ═══════════════════════════════════════════
// SMTP email transport (gmail / custom SMTP) — tenant + platform scopes
// ═══════════════════════════════════════════

export type EmailTransportProvider = 'gmail' | 'smtp';

export type EmailTransportScope = 'TENANT' | 'PLATFORM';

export type EmailVerificationStatus = 'PENDING' | 'VERIFIED' | 'DISABLED';

export type EmailHealthStatus = 'UNKNOWN' | 'OK' | 'FAILING';

/** What GET /config returns — the password is never echoed back. */
export interface EmailTransportConfigMasked {
    scope: EmailTransportScope;
    tenant_id: string | null;
    provider: EmailTransportProvider;
    smtp_host: string;
    smtp_port: number;
    smtp_secure: boolean;
    email: string;
    password_masked: string;
    from_name: string | null;
    reply_to_email: string | null;
    verification_status: EmailVerificationStatus;
    verification_sent_at: string | null;
    verified_at: string | null;
    last_verify_error: string | null;
    health_status: EmailHealthStatus;
    health_checked_at: string | null;
    health_error: string | null;
    consecutive_failures: number;
    last_ok_at: string | null;
    last_tested_at: string | null;
    last_test_status: 'OK' | 'FAILED' | null;
    last_test_error: string | null;
    updated_at: string;
}

export interface UpsertEmailTransportInput {
    provider: EmailTransportProvider;
    email: string;
    password: string;
    from_name?: string;
    reply_to_email?: string;
    smtp_host?: string;
    smtp_port?: number;
}

/** Result of the verification mail sent immediately after an upsert. */
export interface EmailVerificationOutcome {
    sent: boolean;
    sent_to?: string;
    expires_at?: string;
    error?: string;
}

export interface UpsertEmailTransportResult {
    config: EmailTransportConfigMasked;
    verification: EmailVerificationOutcome;
}

export interface VerifyEmailTransportInput {
    code: string;
}

export interface VerifyEmailTransportResult {
    verified: true;
    config: EmailTransportConfigMasked;
}

export interface ResendEmailVerificationResult {
    sent: true;
    sent_to: string;
    expires_at: string;
}

export interface SendTestEmailInput {
    to?: string;
}

/** Gmail App Passwords are always 16 characters (spaces stripped). */
export const GMAIL_APP_PASSWORD_LENGTH = 16;

export const SMTP_PROVIDER_LABELS: Record<EmailTransportProvider, string> = {
    gmail: 'Gmail',
    smtp: 'Custom SMTP',
};
